/**
 * v3.15-1167 — Ghost Intelligence™ public page.
 * Introduces Ghost as the operational intelligence layer of Prestigious Hub.
 */
import { Link } from 'react-router-dom';
import { ArrowRight, MessageSquare, FileText, Clock, TrendingUp, RotateCcw, ShieldCheck } from 'lucide-react';
import PublicLayout from '../components/PublicLayout';
import EndPageCTA from '../components/EndPageCTA';
import GhostLogo from '../components/GhostLogo';
import { HubDashboardScreenshot, HubCommunicationsScreenshot } from '../components/HubProductImagery';

const BEFORE = [
  { Icon: MessageSquare, title: 'Answers funding questions', body: 'On-site and inside the Hub. 24/7.' },
  { Icon: FileText,      title: 'Captures leads',            body: 'Qualifies prospects and routes to Concierges.' },
  { Icon: Clock,         title: 'Books appointments',        body: 'Schedules calls without your team lifting a finger.' },
  { Icon: ShieldCheck,   title: 'Starts applications',       body: 'Begins intake before a human ever joins.' },
];
const DURING = [
  { Icon: FileText,    title: 'Tracks documentation',    body: 'Knows what is missing and chases the gap.' },
  { Icon: Clock,       title: 'Monitors timelines',      body: 'Flags files at risk of aging out.' },
  { Icon: MessageSquare,title:'Improves communication',  body: 'Drafts follow-ups for your Concierge.' },
  { Icon: ShieldCheck, title: 'Assists Concierges',      body: 'Surfaces the next best action on every file.' },
];
const AFTER = [
  { Icon: TrendingUp, title: 'Identifies refinance opportunities', body: 'Watches for windows where a better deal exists.' },
  { Icon: RotateCcw,  title: 'Routes into CreditForward360',       body: 'Credit improvement re-entry for not-yet clients.' },
  { Icon: TrendingUp, title: 'Surfaces future funding',            body: 'Brings clients back when they qualify for more.' },
];

const Tile = ({ Icon, title, body }) => (
  <div className="bg-white border border-slate-100 rounded-lg p-5">
    <Icon className="h-5 w-5 text-primary mb-3" />
    <h3 className="font-heading text-sm font-bold text-slate-900 mb-1">{title}</h3>
    <p className="text-xs text-slate-600 leading-relaxed">{body}</p>
  </div>
);

const GhostPage = () => (
  <PublicLayout testId="ghost-page">
    {/* HERO */}
    <section className="bg-slate-950 text-white px-6 py-32 lg:py-48 border-b border-gold/20 relative overflow-hidden">
      {/* subtle gold radial accent */}
      <div aria-hidden className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-gold/10 rounded-full blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-7">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-8">Ghost<sup className="text-[8px]">™</sup> · AI Operations Assistant</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-8xl font-bold leading-[1.0] tracking-tight mb-8">
            Meet Ghost.<br />
            <span className="text-gradient-gold">Your AI operations partner.</span>
          </h1>
          <p className="text-xl sm:text-2xl text-slate-300 font-light max-w-2xl leading-relaxed">
            Ghost is the operational intelligence layer behind Prestigious Hub. It continuously assists our Application Concierge team — monitoring files, identifying opportunities, drafting communication, and keeping every deal moving.
          </p>
          <p className="mt-6 text-base text-gold/90">Humans + AI. Not humans <em>or</em> AI.</p>
        </div>
        <div className="lg:col-span-5 flex items-center justify-center">
          <div className="relative">
            <div aria-hidden className="absolute inset-0 bg-gold/20 blur-3xl rounded-full scale-90" />
            <GhostLogo size="hero" className="relative drop-shadow-2xl" testId="ghost-hero-mark" />
          </div>
        </div>
      </div>
    </section>

    {/* Before / During / After */}
    <section className="bg-slate-50 px-6 py-24 lg:py-28">
      <div className="max-w-6xl mx-auto space-y-16">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Before the deal</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-primary leading-[1.1] mb-8">Ghost begins the relationship.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{BEFORE.map((b) => <Tile key={b.title} {...b} />)}</div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">During the deal</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-primary leading-[1.1] mb-8">Ghost works alongside the Concierge.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{DURING.map((b) => <Tile key={b.title} {...b} />)}</div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">After the deal</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-primary leading-[1.1] mb-8">Ghost keeps the relationship alive.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{AFTER.map((b) => <Tile key={b.title} {...b} />)}</div>
        </div>
      </div>
    </section>

    {/* PROOF — paired with real Hub screenshots */}
    <section className="bg-white px-6 py-28 lg:py-36">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Inside Prestigious Hub</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-primary leading-[1.1] mb-6">Ghost watches every deal. So you don&rsquo;t have to.</h2>
          <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
            From the dashboard to the document checklist to the messaging thread — Ghost is monitoring every file in real time, flagging risk, and arming your Concierge with the next best move.
          </p>
        </div>
        <HubDashboardScreenshot />
      </div>
    </section>

    <section className="bg-slate-50 px-6 py-28 lg:py-36">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-16 items-center">
        <HubCommunicationsScreenshot />
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Always-on intake</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-primary leading-[1.1] mb-6">Ghost answers when the office is closed.</h2>
          <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
            Funding questions, program eligibility, soft-pull pre-approvals — Ghost is on call 24/7. Then it hands the prospect to your Concierge with full context already captured.
          </p>
        </div>
      </div>
    </section>

    {/* Closing */}
    <section className="bg-gradient-to-b from-slate-900 to-primary text-white px-6 py-28 lg:py-36">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-8">
          Ghost begins the relationship.<br />
          <span className="text-gradient-gold">Your Concierge finishes the deal.</span>
        </h2>
        <Link to="/platform" className="inline-flex items-center gap-2 bg-gradient-gold text-slate-900 font-semibold px-6 py-3 rounded hover:shadow-gold transition-all" data-testid="ghost-cta-platform">
          See it inside the Platform <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>

    <EndPageCTA testId="ghost-end-cta" />
  </PublicLayout>
);

export default GhostPage;
